"use client";

import {
  forwardRef,
  useCallback,
  useEffect,
  useRef,
  type VideoHTMLAttributes,
} from "react";
import { captureClientEvent } from "@/lib/analytics/client";
import { ANALYTICS_EVENTS } from "@/lib/analytics/events";
import { isKlipyCdnUrl } from "@/lib/klipy-cdn";
import {
  isMediaMotionActive,
  onceMediaMotionQuiet,
} from "@/lib/media/media-motion";
import { observeVideoProximity } from "@/lib/media/video-proximity";

export type HlsVideoProps = Omit<
  VideoHTMLAttributes<HTMLVideoElement>,
  "src"
> & {
  src: string;
  hlsSrc?: string;
  mimeType?: string;
};
const HLS_MIME = "application/vnd.apple.mpegurl";
/** A buffered player whose clock has not moved for this long is frozen. */
const STALL_MS = 3000;
/** Still waiting on the network: give a slow connection time before restarting it. */
const LOADING_STALL_MS = 8000;
/** `HTMLMediaElement.HAVE_FUTURE_DATA`: a frame beyond the current one is buffered. */
const HAVE_FUTURE_DATA = 3;
/**
 * A neighbour's warm-up waits this long after it turns hot, so the autoplay
 * provider (a separate IntersectionObserver, delivered in the same frame or
 * the next) has claimed the on-screen player first and can be yielded to.
 */
const WARM_UP_DELAY_MS = 150;
/** A start that has not produced a frame by now stops holding neighbours back. */
const START_YIELD_MAX_MS = 2500;

/**
 * Warm-ups yield to the player the viewer is actually looking at. On a cold
 * /hiives load in the iOS app the on-screen card and its two hot neighbours
 * all fetched their opening segment at once, and the on-screen one got its
 * data LAST (~830 ms, versus ~500 ms for neighbours nobody could see).
 */
const livePlayers = new Set<HTMLVideoElement>();
/** Real (non-warm-up) play() calls still waiting on their first frame. */
const startingPlayers = new Set<HTMLVideoElement>();
const pendingWarmUps = new Set<() => void>();

function anotherPlayerStarting(self: HTMLVideoElement) {
  for (const player of livePlayers) {
    if (player === self) continue;
    if (startingPlayers.has(player)) return true;
    // Claimed by the autoplay provider, its play() not dispatched yet — the
    // few ms in which a neighbour's warm-up timer used to win the race.
    if (
      player.dataset.autoplayActive === "true" &&
      player.paused &&
      player.readyState < HAVE_FUTURE_DATA
    )
      return true;
  }
  return false;
}

function settleStart(video: HTMLVideoElement) {
  if (!startingPlayers.delete(video) || startingPlayers.size) return;
  const waiting = [...pendingWarmUps];
  pendingWarmUps.clear();
  for (const warmUp of waiting) warmUp();
}

/** Public Hiive video is HLS-only. Local previews and external GIFs keep their native format. */
export const HlsVideo = forwardRef<HTMLVideoElement, HlsVideoProps>(
  function HlsVideo(
    {
      src,
      hlsSrc,
      mimeType: _mimeType,
      preload = "metadata",
      onError,
      ...props
    },
    ref,
  ) {
    const element = useRef<HTMLVideoElement | null>(null);
    const attach = useCallback(
      (node: HTMLVideoElement | null) => {
        element.current = node;
        if (typeof ref === "function") return ref(node);
        if (ref) ref.current = node;
      },
      [ref],
    );
    useEffect(() => {
      const video = element.current;
      if (!video) return;
      const playlist = hlsSrc || (/\.m3u8(?:[?#]|$)/i.test(src) ? src : null);
      const preview = /^(blob:|data:)/.test(src) || isKlipyCdnUrl(src);
      const nativeHls = Boolean(video.canPlayType(HLS_MIME));
      // Chromium now reports native HLS support too, but its native loader
      // ignores our segment budget and can defer preload until play(). Keep
      // Safari/WebKit native; use the controlled MSE player elsewhere.
      const appleWebKit =
        /AppleWebKit/i.test(navigator.userAgent) &&
        !/Chrome|Chromium|Edg\/|OPR\/|Android/i.test(navigator.userAgent);
      let path = preview ? "preview" : playlist ? "hlsjs" : "progressive";
      let disposed = false;
      let nearby = false;
      let hot = false;
      let hls: import("hls.js").default | null = null;
      let initialized = false;
      let nativeAttached = false;
      let reportedStart = false;
      let playRequestedAt: number | undefined;
      // `warmed`: this attachment already holds a buffer. `warming`: our own
      // warm-up play() is in flight, so its `playing` is not a real start.
      let warmed = false;
      let warming = false;
      let resumeAt = 0;
      let warmUpTimer: ReturnType<typeof setTimeout> | undefined;
      let cancelQuietWait: (() => void) | undefined;
      let yieldTimer: ReturnType<typeof setTimeout> | undefined;
      let watchdog: ReturnType<typeof setInterval> | undefined;
      let progressTime = -1;
      let progressAt = 0;
      let recoveries = 0;
      const hasFrame = () => {
        for (let i = 0; i < video.buffered.length; i++) {
          if (
            video.buffered.start(i) <= video.currentTime &&
            video.buffered.end(i) > video.currentTime + 0.1
          )
            return true;
        }
        return false;
      };
      const loadAhead = () => {
        if (!hls || !nearby) return;
        // One opening segment for a paused neighbour; a short forward buffer
        // for the active player. Do not download every mounted clip in full.
        hls.config.maxBufferLength = video.paused ? 3 : 6;
        hls.config.maxMaxBufferLength = video.paused ? 3 : 9;
        if (!video.paused || !hasFrame()) hls.startLoad();
      };
      const attachNative = () => {
        path = preview ? "preview" : "native";
        nativeAttached = true;
        // `metadata`, not `auto`. The hls.js path has an explicit segment
        // budget (`loadAhead` below); the native path has none, so `auto` told
        // WebKit to buffer every card inside the proximity window at once.
        // iOS caps how many media elements may load concurrently and silently
        // stalls the ones over the line — which is the "the video just never
        // starts" on a carousel of nine hiive cards. `play()` overrides this
        // for whichever player actually becomes visible (see `onPlay`), and a
        // hot neighbour gets it with its warm-up, once nothing on screen is
        // still starting.
        video.preload = "metadata";
        video.src = playlist || src;
        video.load();
        // A visible player's first play() may have raced source attachment.
        // Safari may only preload metadata, so waiting for canplay to retry
        // can deadlock. Let the visibility owner retry as soon as a source exists.
        video.dispatchEvent(new Event("hiive:video-source-ready"));
        if (video.autoplay) void video.play().catch(() => {});
        else scheduleWarmUp();
      };
      /**
       * Gives a native player the buffer the viewer is about to need.
       *
       * iOS WebKit does not reliably honour `preload="auto"` for media no
       * gesture started (it is widely reported to clamp it to `"metadata"`),
       * so a neighbour that was never played may have no AVPlayerItem: its
       * first play() still has to fetch the variant playlist and a segment,
       * which is the poster sitting still for a second after a swipe. A muted
       * play() is allowed without a gesture and does build the item; pausing
       * on the first `playing` keeps the buffer and a decoded frame.
       * Only the `HOT_VIDEOS` nearest players do this, and never while a player
       * the viewer can see is still starting (see `startingPlayers`).
       */
      const warmUp = (force = false, quiet = false) => {
        if (disposed || !hot || !nativeAttached || preview || warmed) return;
        // Already playing, owned by the autoplay provider, or would be audible.
        if (!video.paused || !video.muted) return;
        if (video.dataset.autoplayActive === "true") return;
        if (!force && anotherPlayerStarting(video)) {
          pendingWarmUps.add(wakeWarmUp);
          // A start that never lands (Low Power Mode refused it, a dead
          // segment) must not hold every neighbour cold behind it.
          yieldTimer ??= setTimeout(() => {
            yieldTimer = undefined;
            pendingWarmUps.delete(wakeWarmUp);
            warmUp(true);
          }, START_YIELD_MAX_MS);
          return;
        }
        // A decoder start while a carousel moves is a dropped frame; this
        // warm-up only prepares a card for later, so it waits for the track to
        // rest or be caught by a finger (frozen in place either way).
        if (!quiet && isMediaMotionActive()) {
          cancelQuietWait ??= onceMediaMotionQuiet(() => {
            cancelQuietWait = undefined;
            warmUp(force, true);
          });
          return;
        }
        if (yieldTimer !== undefined) clearTimeout(yieldTimer);
        yieldTimer = undefined;
        warmed = true;
        video.preload = "auto";
        warming = true;
        void video.play().catch(() => {
          // Low Power Mode refuses this outright; the real play() still works
          // after the next tap. `warmed` stays set so it is not retried per frame.
          warming = false;
        });
      };
      const wakeWarmUp = () => warmUp();
      const scheduleWarmUp = () => {
        if (warmUpTimer !== undefined || warmed) return;
        warmUpTimer = setTimeout(() => {
          warmUpTimer = undefined;
          warmUp();
        }, WARM_UP_DELAY_MS);
      };
      const cancelWarmUp = () => {
        if (warmUpTimer !== undefined) clearTimeout(warmUpTimer);
        warmUpTimer = undefined;
        if (yieldTimer !== undefined) clearTimeout(yieldTimer);
        yieldTimer = undefined;
        pendingWarmUps.delete(wakeWarmUp);
        cancelQuietWait?.();
        cancelQuietWait = undefined;
      };
      /**
       * Hands the decoder back when this player leaves the lookahead window.
       *
       * hls.js neighbours free themselves through `stopLoad()`; a native player
       * has no equivalent, so without this every card the user has scrolled
       * past keeps its source — and on iOS that is a finite resource the next
       * card needs. `PosterVideo` puts its cover back on `emptied`, so the
       * release shows the poster rather than black (`watchVideoFrame`).
       */
      const releaseNative = () => {
        // A local preview (blob:/data:) is a composer surface, not a feed card:
        // there is no decoder pressure to relieve and re-attaching would throw
        // away the scrub position the user just set.
        if (!nativeAttached || preview) return;
        nativeAttached = false;
        initialized = false;
        warmed = false;
        warming = false;
        cancelWarmUp();
        settleStart(video);
        video.pause();
        video.removeAttribute("src");
        video.load();
      };
      const initialize = async () => {
        if (initialized || disposed) return;
        initialized = true;
        if (preview || (playlist && nativeHls && appleWebKit)) {
          attachNative();
          return;
        }
        if (!playlist) return; // Retain the poster for an unrepaired legacy asset.
        try {
          const { default: Hls } = await import("hls.js");
          if (disposed) return;
          if (!nearby) {
            initialized = false;
            return;
          }
          if (!Hls.isSupported()) {
            if (nativeHls) attachNative();
            return;
          }
          hls = new Hls({
            autoStartLoad: false,
            startLevel: 0,
            maxBufferLength: 3,
            maxMaxBufferLength: 3,
            maxBufferSize: 2 * 1024 * 1024,
            backBufferLength: 0,
            capLevelToPlayerSize: true,
          });
          hls.on(Hls.Events.MANIFEST_PARSED, () => {
            loadAhead();
            video.dispatchEvent(new Event("hiive:video-source-ready"));
            if (nearby && video.autoplay) void video.play().catch(() => {});
          });
          hls.on(Hls.Events.FRAG_BUFFERED, () => {
            // Separate audio/video tracks can buffer independently. Stopping
            // after just the audio fragment leaves the poster stuck forever.
            if (video.paused && hasFrame()) hls?.stopLoad();
          });
          let recovered = false;
          hls.on(Hls.Events.ERROR, (_event, data) => {
            if (!data.fatal || disposed) return;
            if (data.type === Hls.ErrorTypes.MEDIA_ERROR && !recovered) {
              recovered = true;
              hls?.recoverMediaError();
            } else {
              hls?.destroy();
              hls = null;
              video.dispatchEvent(new Event("error"));
            }
          });
          hls.attachMedia(video);
          hls.loadSource(playlist);
        } catch {
          if (!disposed) video.dispatchEvent(new Event("error"));
        }
      };
      /**
       * Restarts a player that says it is playing but has stopped moving.
       *
       * WebKit's native HLS can wedge with `paused === false` and a frozen
       * clock — at the loop seam, after the app returns from the background,
       * or when a segment request dies mid-flight — and nothing fires to say
       * so. The poster covers a re-attach, so a restart is invisible; a frozen
       * frame is not. Two attempts per stall, then leave it for the next play().
       */
      const recover = () => {
        recoveries += 1;
        if (recoveries > 2) return;
        if (hls) {
          if (recoveries === 1) hls.startLoad();
          else hls.recoverMediaError();
          return;
        }
        if (!nativeAttached) return;
        if (recoveries === 1 && video.readyState >= HAVE_FUTURE_DATA) {
          // A seek to where it already is is often enough to unstick
          // AVPlayer at the loop seam.
          const at = video.currentTime;
          video.currentTime = at;
          return;
        }
        resumeAt = video.currentTime;
        releaseNative();
        // attachNative announces the new source; the autoplay provider replays
        // it if it is still the one on screen.
        void initialize();
      };
      const checkProgress = () => {
        const now = performance.now();
        if (video.paused || warming || document.hidden || !nearby) {
          progressAt = now;
          return;
        }
        if (video.currentTime !== progressTime) {
          progressTime = video.currentTime;
          progressAt = now;
          recoveries = 0;
          return;
        }
        const limit =
          video.readyState >= HAVE_FUTURE_DATA ? STALL_MS : LOADING_STALL_MS;
        if (now - progressAt < limit) return;
        progressAt = now;
        recover();
      };
      const stopWatchdog = () => {
        if (watchdog !== undefined) clearInterval(watchdog);
        watchdog = undefined;
      };
      const onPlay = () => {
        // A real start, not our own warm-up: neighbours yield to it until it
        // has a frame on screen.
        if (!warming && video.readyState < HAVE_FUTURE_DATA)
          startingPlayers.add(video);
        playRequestedAt = performance.now();
        nearby = true;
        // The one player that is actually on screen gets the full buffer; see
        // the budget note in `attachNative`.
        if (nativeAttached) video.preload = "auto";
        void initialize();
        loadAhead();
        progressTime = video.currentTime;
        progressAt = performance.now();
        if (watchdog === undefined) watchdog = setInterval(checkProgress, 1000);
      };
      const onPause = () => {
        settleStart(video);
        stopWatchdog();
        if (hasFrame()) hls?.stopLoad();
      };
      const onLoadedMetadata = () => {
        // Put a recovered player back where it froze instead of at 0:00.
        if (resumeAt > 0 && resumeAt < video.duration)
          video.currentTime = resumeAt;
        resumeAt = 0;
      };
      const onPlaying = () => {
        settleStart(video);
        if (warming) {
          warming = false;
          // The provider may have claimed this player while the warm-up was
          // pending; then this is a real start and must keep going.
          if (video.dataset.autoplayActive !== "true") {
            video.pause();
            return;
          }
        }
        if (reportedStart || disposed) return;
        reportedStart = true;
        captureClientEvent(ANALYTICS_EVENTS.VIDEO_PLAYBACK_STARTED, {
          path,
          has_hls: Boolean(playlist),
          // PosterVideo covers with an <img> instead of the attribute.
          has_poster: Boolean(video.poster || video.dataset.hasPoster),
          startup_ms:
            playRequestedAt === undefined
              ? undefined
              : Math.round(performance.now() - playRequestedAt),
        });
      };
      video.addEventListener("play", onPlay);
      video.addEventListener("pause", onPause);
      video.addEventListener("playing", onPlaying);
      video.addEventListener("loadedmetadata", onLoadedMetadata);
      livePlayers.add(video);
      const stopObserving = observeVideoProximity(video, (warmth) => {
        nearby = warmth !== "cold";
        hot = warmth === "hot";
        if (nearby) {
          void initialize();
          loadAhead();
          scheduleWarmUp();
        } else {
          hls?.stopLoad();
          video.pause();
          releaseNative();
          recoveries = 0;
        }
      });
      return () => {
        disposed = true;
        livePlayers.delete(video);
        cancelWarmUp();
        settleStart(video);
        stopObserving();
        stopWatchdog();
        video.removeEventListener("play", onPlay);
        video.removeEventListener("pause", onPause);
        video.removeEventListener("playing", onPlaying);
        video.removeEventListener("loadedmetadata", onLoadedMetadata);
        hls?.destroy();
        video.pause();
        video.removeAttribute("src");
        video.load();
      };
    }, [src, hlsSrc]);
    return (
      <video
        crossOrigin="anonymous"
        {...props}
        ref={attach}
        preload={preload}
        onError={(event) => {
          captureClientEvent(ANALYTICS_EVENTS.VIDEO_PLAYBACK_FAILED, {
            path:
              hlsSrc || /\.m3u8(?:[?#]|$)/i.test(src) ? "hls" : "progressive",
            current_src: event.currentTarget.currentSrc,
          });
          onError?.(event);
        }}
      />
    );
  },
);
